import { FATOR_COMISSAO_FORA_SUL } from './foraSul';

export interface CommissionRuleRenovacaoDelta {
  id?: string;
  commission_rule_id: string;
  faixa_delta: number;
  percentual: number;
}

export interface ItemComRenovacaoDelta {
  produto?: string | null;
  subtotal?: number | null;
  linhas_telefone?: unknown;
  /** Fora sul paga metade (Douglas 26/09/2026). O `faturamentoMp` recebido já é a base comissionável. */
  fora_sul?: boolean | null;
}

export interface ComissaoRenovacaoDeltaDetalhe {
  baseTotalMp: number;
  baseLegada: number;
  baseAdicionada: number;
  basePorFaixa: Record<number, number>;
  percentualPorFaixa: Record<number, number>;
  comissaoPorFaixa: Record<number, number>;
  comissaoLegada: number;
  comissaoAdicionada: number;
  comissaoTotal: number;
  linhasComDelta: number;
}

const numero = (valor: unknown): number => {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
  if (typeof valor !== 'string') return 0;
  const normalizado = valor.trim().replace(/\s/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.');
  const parsed = Number(normalizado);
  return Number.isFinite(parsed) ? parsed : 0;
};

const linhasDoItem = (valor: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(valor)) return valor.filter((linha): linha is Record<string, unknown> => !!linha && typeof linha === 'object');
  if (typeof valor !== 'string' || !valor.trim()) return [];
  try {
    const parsed = JSON.parse(valor);
    return Array.isArray(parsed)
      ? parsed.filter((linha): linha is Record<string, unknown> => !!linha && typeof linha === 'object')
      : [];
  } catch {
    return [];
  }
};

export function calcularComissaoRenovacaoDelta(
  faturamentoMp: number,
  commissionRuleId: string,
  percentualLegado: number,
  matriz: CommissionRuleRenovacaoDelta[] = [],
  itens: ItemComRenovacaoDelta[] = [],
): ComissaoRenovacaoDeltaDetalhe {
  const basePorFaixa: Record<number, number> = Object.fromEntries(
    Array.from({ length: 7 }, (_, index) => [index + 1, 0]),
  );
  const percentualPorFaixa: Record<number, number> = Object.fromEntries(
    Array.from({ length: 7 }, (_, index) => [index + 1, percentualLegado]),
  );
  let baseAdicionada = 0;
  let linhasComDelta = 0;

  for (const regra of matriz) {
    if (regra.commission_rule_id !== commissionRuleId) continue;
    const faixa = numero(regra.faixa_delta);
    if (faixa >= 1 && faixa <= 7) percentualPorFaixa[faixa] = numero(regra.percentual);
  }

  for (const item of itens) {
    const produto = String(item.produto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
    if (produto !== 'RENOVACAO') continue;
    const peso = item.fora_sul === true ? FATOR_COMISSAO_FORA_SUL : 1;
    for (const linha of linhasDoItem(item.linhas_telefone)) {
      const faixa = numero(linha.faixa ?? linha.faixa_apurada);
      const baseRenovada = Math.max(0, numero(linha.valor_novo ?? linha.receita_renovada ?? linha.ticket_novo));
      if (faixa < 1 || faixa > 7 || baseRenovada <= 0) continue;
      basePorFaixa[faixa] += baseRenovada * peso;
      baseAdicionada += Math.max(0, numero(linha.receita_adicionada)) * peso;
      linhasComDelta += 1;
    }
  }

  const baseDelta = Object.values(basePorFaixa).reduce((soma, valor) => soma + valor, 0);
  const baseTotalMp = Math.max(0, numero(faturamentoMp));
  const baseLegada = Math.max(0, baseTotalMp - baseDelta - baseAdicionada);
  const comissaoPorFaixa: Record<number, number> = {};
  for (let faixa = 1; faixa <= 7; faixa += 1) {
    comissaoPorFaixa[faixa] = (basePorFaixa[faixa] * percentualPorFaixa[faixa]) / 100;
  }
  const comissaoLegada = (baseLegada * percentualLegado) / 100;
  const comissaoAdicionada = (baseAdicionada * percentualLegado) / 100;
  const comissaoTotal = comissaoLegada
    + comissaoAdicionada
    + Object.values(comissaoPorFaixa).reduce((soma, valor) => soma + valor, 0);

  return {
    baseTotalMp,
    baseLegada,
    baseAdicionada,
    basePorFaixa,
    percentualPorFaixa,
    comissaoPorFaixa,
    comissaoLegada,
    comissaoAdicionada,
    comissaoTotal,
    linhasComDelta,
  };
}
